import { Table, Input, Space } from 'antd'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badges } from '../'
import { BitazzaText, Spinner } from '../../../../components'
import { arrayMove } from '../../../../util/arrayMethods'
import { formatNumber } from '../../../../util/formatNumber'
import { getColor } from '../../../../util/getColor'
import { HeaderBadges } from '../HeaderBadges'
import { UserRank } from '../UserRank/UserRank'
import Styles from './Top100.module.scss'
import scrollIntoView from 'scroll-into-view'
import { SVGs } from '../../../../assets'
import { useNavigate, createSearchParams } from 'react-router-dom'

interface Props {
    rank?: LeaderBoard[] | []
    user?: usersRequest
    notTop3?: boolean
    onSearchUserTag?: (value: string) => void
    searchResults?: LeaderBoard[] | []
    status: string
    selectedTab: string
}

type tableType = {
    rank: number
    name: string
    pnl: string
    type: string
    id: number
}
export const Top100: FC<Props> = ({
    rank,
    user,
    notTop3,
    onSearchUserTag,
    searchResults,
    status,
    selectedTab,
}) => {
    const { t } = useTranslation()

    const columns = [
        {
            title: t('txt_rank'),
            dataIndex: 'rank',
            key: 'rank',
            width: '14%',
            render(text: string) {
                return (
                    <BitazzaText
                        content={text ? text : 'N/A'}
                        color="#fff"
                        format="t18"
                        noWrap
                    />
                )
            },
        },
        {
            title: t('txt_name'),
            dataIndex: 'name',
            key: 'name',
            width: '58%',
            render(text: string, record: any) {
                return (
                    <div className={Styles.tableName}>
                        <Badges type={record.type} table />
                        <BitazzaText content={text} color="#fff" format="t18" />
                    </div>
                )
            },
        },
        {
            title: '%PNL',
            dataIndex: 'pnl',
            key: 'pnl',
            render(text: number) {
                return (
                    <div className={Styles.pnlWrapper}>
                        <BitazzaText
                            content={`${formatNumber(
                                +text,
                                false,
                                true,
                                true,
                                true
                            )}%`}
                            color={getColor(+text, true)}
                            format="t18"
                            strong
                            noWrap
                        />
                    </div>
                )
            },
        },
    ]
    const navigate = useNavigate()

    const [data, setData] = useState<tableType[]>([])
    const [shouldScroll, setShouldScroll] = useState<boolean>(false)
    const [searchText, setSearchText] = useState<string>('')
    const [scrollTable, setScrollTable] = useState<number>(362)
    const [tableData, setTableData] = useState<LeaderBoard[] | [] | undefined>(
        []
    )

    useEffect(() => {
        const sliceNumber = notTop3 ? 0 : 3
        if (tableData) {
            setData(
                tableData.slice(sliceNumber, tableData.length).map((row) => ({
                    key: row.rank,
                    rank: row.rank,
                    name: row?.additional_user_info?.profileName,
                    pnl: row.score,
                    type: row.tier?.name,
                    id: row.additional_user_info?.accountId,
                }))
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData])

    useEffect(() => {
        setTableData(rank)
    }, [rank])

    const handleScroll = () => {
        const rows = document.querySelector('.scroll-row') as any
        scrollIntoView(rows, {
            align: {
                topOffset: 10,
            },
        })
    }

    useEffect(() => {
        if (shouldScroll && notTop3) {
            handleScroll()
        }
    }, [shouldScroll, notTop3])

    const handleRowClassName = (record: tableType) => {
        if (user && user.rank === record.rank) {
            setTimeout(() => setShouldScroll(true), 0)
            return 'scroll-row'
        } else {
            return ''
        }
    }

    useEffect(() => {
        setTableData(searchResults)
    }, [searchResults])

    const onSearch = () => {
        onSearchUserTag && onSearchUserTag(searchText)
    }

    const onChangeHandler = (value: any) => {
        setSearchText(value.target.value)
        if (!value.target.value) {
            setTableData(rank)
        }
    }

    useEffect(() => {
        if (selectedTab === 'myRanking') {
            setScrollTable(window.innerHeight - 228)
        } else {
            setScrollTable(window.innerHeight - 160)
        }
    }, [selectedTab])

    const locale = {
        emptyText: (
            <Space direction="vertical" size={16}>
                {status !== 'pending' && (
                    <>
                        <img src={SVGs.searchNotFound} alt="Search Not Found" />
                        <BitazzaText
                            content={t('txt_no_result')}
                            format="t20"
                            color="#fff"
                        />
                    </>
                )}
            </Space>
        ),
    }

    return (
        <div>
            {rank && rank.length ? (
                <>
                    {!notTop3 ? (
                        <HeaderBadges
                            data={arrayMove(rank.slice(0, 3), 0, 1)}
                        />
                    ) : null}

                    {onSearchUserTag ? (
                        <div className={Styles.searchContainer}>
                            <Input
                                placeholder={t('txt_search_challenger')}
                                onChange={onChangeHandler}
                                suffix={
                                    <img
                                        src={SVGs.searchIcon}
                                        alt="selected_icon"
                                        width={16}
                                        height={18}
                                        onClick={onSearch}
                                    />
                                }
                            />
                        </div>
                    ) : null}

                    <Table
                        className={Styles.table}
                        bordered={false}
                        rowClassName={(record) => handleRowClassName(record)}
                        dataSource={data}
                        columns={columns}
                        showHeader={data.length ? true : false}
                        pagination={false}
                        onRow={(record) => {
                            return {
                                onClick: () =>
                                    navigate({
                                        pathname: '/my-profile',
                                        search: createSearchParams({
                                            search: record.id.toString(),
                                        }).toString(),
                                    }),
                            }
                        }}
                        loading={status === 'pending'}
                        scroll={{ y: scrollTable }}
                        locale={locale}
                    />

                    {user ? (
                        <UserRank
                            name={user?.additional_user_info?.profileName}
                            tier={user.tier?.name || ''}
                            score={user.score || ''}
                            rank={user.rank || 'N/A'}
                            rankingCriteria={user?.ranking_criteria}
                        />
                    ) : (
                        <Spinner />
                    )}
                </>
            ) : null}
        </div>
    )
}
